import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ChevronDown, ChevronRight, RefreshCw, ServerCrash, Trash2 } from "lucide-react"
import { smartoltService, type SmartOLTOnuRow } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { displayName } from "./smartoltConfiguredUtils.ts"
import { queueSmartOltOnuCommand } from "./smartoltOnuCommands.ts"

type OltGroup = {
  oltId: string
  oltName: string
  rows: SmartOLTOnuRow[]
}

function configuredPortHref(row: SmartOLTOnuRow) {
  const query = new URLSearchParams()
  if (row.olt_id) query.set("olt", String(row.olt_id))
  if (row.board) query.set("board", String(row.board))
  if (row.port) query.set("port", String(row.port))
  const text = query.toString()
  return text ? `${SMARTOLT_PATHS.configured}?${text}` : SMARTOLT_PATHS.configured
}

function portLabel(row: SmartOLTOnuRow) {
  if (row.board && row.port) return `${row.board}/${row.port}`
  return row.pon_port || row.port || "—"
}

function offlineLabel(row: SmartOLTOnuRow) {
  if (row.offline_days != null) return `${row.offline_days}d`
  if (!row.last_online_at) return "—"
  const then = new Date(row.last_online_at).getTime()
  if (Number.isNaN(then)) return "—"
  const days = Math.max(0, Math.floor((Date.now() - then) / 86400000))
  return `${days}d`
}

export function SmartOLTTerminatedPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [rows, setRows] = useState<SmartOLTOnuRow[]>([])
  const [openOlts, setOpenOlts] = useState<Set<string>>(new Set())
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try { await smartoltService.runSyncNow(5) } catch { /* keep listing */ }
      }
      const onuRows = await smartoltService.getTerminatedOnus({ limit: 2000 })
      setRows(Array.isArray(onuRows) ? onuRows : [])
    } catch {
      toast.error("Failed to load potentially terminated ONUs")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const groups = useMemo<OltGroup[]>(() => {
    const byOlt = new Map<string, OltGroup>()
    rows.forEach((row) => {
      const oltId = String(row.olt_id || "unknown")
      const existing = byOlt.get(oltId)
      if (existing) { existing.rows.push(row); return }
      byOlt.set(oltId, { oltId, oltName: row.olt_name || `OLT ${oltId}`, rows: [row] })
    })
    return [...byOlt.values()].sort((a, b) => a.oltName.localeCompare(b.oltName))
  }, [rows])

  useEffect(() => {
    if (groups.length === 1) setOpenOlts(new Set([groups[0].oltId]))
  }, [groups])

  const toggleOlt = (oltId: string) => {
    setOpenOlts((prev) => {
      const next = new Set(prev)
      if (next.has(oltId)) next.delete(oltId)
      else next.add(oltId)
      return next
    })
  }

  const deleteOnu = async (row: SmartOLTOnuRow) => {
    const name = displayName(row)
    const ok = window.confirm(
      `Delete ONU "${name}" from the OLT?\n\nThis removes it from the OLT and cannot be undone.`,
    )
    if (!ok) return
    setBusyId(row.onu_external_id)
    try {
      await queueSmartOltOnuCommand(row.onu_external_id, "delete_onu_by_external_id")
      toast.success("Delete queued")
      await load(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to delete ONU")
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Potentially Terminated ONUs/ONTs</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Offline 15+ days with unknown disconnection cause (excludes fiber cut & power failure). Grouped by OLT.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rows.length > 0 && (
            <span className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-700">
              {rows.length} ONUs · {groups.length} OLTs
            </span>
          )}
          <button type="button" onClick={() => load(false, true)} disabled={refreshing}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        {loading ? (
          <div className="px-4 py-12 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
            <p className="text-slate-500 text-sm">Loading…</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <ServerCrash className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-800">All clear!</p>
            <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
              No potentially terminated ONUs — nothing has been dark for 15+ days with an unknown cause.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {groups.map((group) => {
              const open = openOlts.has(group.oltId)
              return (
                <div key={group.oltId}>
                  {/* Group header */}
                  <div className={`flex items-center gap-2 px-4 py-3 transition-colors cursor-pointer hover:bg-slate-50 ${open ? "bg-slate-50/80" : ""}`}
                    onClick={() => toggleOlt(group.oltId)}>
                    <button type="button" aria-expanded={open} className="flex items-center">
                      {open
                        ? <ChevronDown className="h-4 w-4 text-blue-600" />
                        : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    </button>
                    <Link to={SMARTOLT_PATHS.oltDetail(group.oltId)} onClick={(e) => e.stopPropagation()}
                      className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                      {group.oltName}
                    </Link>
                    <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      {group.rows.length} ONUs
                    </span>
                  </div>

                  {/* Expanded ONU table */}
                  {open && (
                    <div className="overflow-x-auto border-t border-slate-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            {["ONU", "Port", "SN", "Cause", "Offline", "Action"].map((h, i) => (
                              <th key={h} className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {group.rows.map((row) => (
                            <tr key={row.onu_external_id} className="transition-colors hover:bg-slate-50">
                              <td className="px-4 py-2.5">
                                <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)}
                                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline">
                                  {displayName(row)}
                                </Link>
                              </td>
                              <td className="px-4 py-2.5">
                                <Link to={configuredPortHref(row)} className="font-mono text-xs text-blue-600 hover:text-blue-800 font-medium">
                                  {portLabel(row)}
                                </Link>
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{row.sn || row.mac || "—"}</td>
                              <td className="px-4 py-2.5 text-slate-600">{row.cause || "Unknown"}</td>
                              <td className="px-4 py-2.5">
                                <span className="rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                  {offlineLabel(row)}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button type="button" title={`Delete ${displayName(row)}`}
                                  disabled={busyId === row.onu_external_id}
                                  onClick={() => deleteOnu(row)}
                                  className="inline-flex h-7 items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50 transition-colors shadow-xs">
                                  <Trash2 className="h-3 w-3" />
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
